// src/Component/CTA.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, ShieldCheck, Zap, BadgeCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const CTA: React.FC = () => {
  return (
    <section className="relative py-24 md:py-32 bg-black overflow-hidden flex flex-col items-center justify-center min-h-[600px] font-[Urbanist]">
      
      {/* =========================================
          1. BACKGROUND MAGIC (Orbital Ring Animation)
          ========================================= */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        {/* Deep Space Dark Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0b1215] to-black z-0" />
        
        {/* Subtle Center Glow for text readability */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[800px] h-[600px] md:h-[800px] bg-blue-900/30 rounded-full blur-[150px] z-0" />

        {/* 3D Animated Orbital Wave - Stays in position, only rotates */}
        <div className="absolute inset-0 flex items-center justify-center opacity-70 z-0" style={{ perspective: '1200px' }}>
          <div className="relative w-full h-full flex items-center justify-center scale-150 md:scale-[1.8]">
            {Array.from({ length: 18 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-[50%] border-[1.5px]"
                style={{
                  // Tight formation - Rings won't spread out
                  width: `${350 + i * 25}px`,
                  height: `${120 + i * 8}px`,
                  borderColor: `hsla(${210 + i * 4}, 100%, 65%, 0.45)`, 
                  boxShadow: `0 0 10px hsla(${210 + i * 4}, 100%, 65%, 0.1)`,
                  willChange: "transform"
                }}
                initial={{ rotateX: 70, rotateZ: i * 10 }}
                animate={{
                  // Orbital rotation only - No scaling or spreading
                  rotateZ: [i * 10, (i * 10) + 360],
                }}
                transition={{
                  duration: 30 + i * 2, // Smooth, slow constant rotation
                  repeat: Infinity, 
                  ease: "linear" 
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* =========================================
          2. MAIN CONTENT (Text & Buttons)
          ========================================= */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mt-10">
        
        {/* Floating Badge */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-black/40 border border-white/20 text-emerald-400 text-xs md:text-sm font-bold mb-8 backdrop-blur-md"
        >
          <Sparkles size={16} className="text-emerald-400" />
          <span className="tracking-wide uppercase">Open your account in 5 minutes</span>
        </motion.div>

        {/* Headlines */}
        <motion.h2 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tight text-white mb-6 leading-[1.05]"
        >
          Ready to elevate your <br className="hidden md:block"/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-green-500 drop-shadow-lg">
           Trading Journey?
          </span>
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-base sm:text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-12 font-medium leading-relaxed"
        >
          Join thousands of profitable traders on <span className="text-white font-bold">Motilal Algo</span>. Experience lightning-fast execution, zero AMC, and premium features at your fingertips.
        </motion.p>

        {/* Actions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-14"
        >
          <Link to="/demat/register" className="w-full sm:w-auto">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto group relative px-8 py-4 md:px-10 md:py-5 bg-white text-slate-900 text-lg md:text-xl font-extrabold rounded-full shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:shadow-[0_0_50px_rgba(255,255,255,0.4)] transition-all"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                Open Free Demat Account <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </motion.button>
          </Link>

          <Link to="/features" className="w-full sm:w-auto">
            <button className="w-full sm:w-auto px-8 py-4 md:px-10 md:py-5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-lg rounded-full transition-colors flex items-center justify-center gap-2 backdrop-blur-md">
              Explore Features
            </button>
          </Link>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm md:text-base text-gray-300 font-semibold bg-black/30 py-3 px-6 rounded-full backdrop-blur-sm border border-white/5"
        >
          <div className="flex items-center gap-2">
            <BadgeCheck size={18} className="text-blue-400" />
            <span>SEBI Registered</span>
          </div>
          <div className="hidden sm:block w-1.5 h-1.5 rounded-full bg-gray-600" />
          <div className="flex items-center gap-2">
            <Zap size={18} className="text-yellow-400" />
            <span>Zero AMC Charges</span>
          </div>
          <div className="hidden sm:block w-1.5 h-1.5 rounded-full bg-gray-600" />
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-400" />
            <span>Bank-Grade Security</span>
          </div>
        </motion.div>

      </div>

      {/* Bottom Line */}
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
    </section>
  );
};

export default CTA;